import { Stack, spacing, Text } from '@scality/core-ui';
import { Button, Input } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useId, useState } from 'react';
import styled from 'styled-components';
import {
  type AbsoluteTimeRange,
  fromDateTimeInputValue,
  parseAbsoluteTimeRange,
  toAbsoluteTimeRange,
  toDateTimeInputValue,
} from './absoluteTimeRange';
import { timeRangeMinutes } from './timeRangePresets';

const MINUTE_MS = 60_000;

/** What the fields open on when the range in force has no length to measure (`all`, nothing yet). */
const FALLBACK_SPAN_MINUTES = 24 * 60;

const REVERSED_BOUNDS = '“To” must be after “From”.';

/**
 * The bounds a range covers right now: an absolute range's own, a preset's measured back from
 * the current minute. The fields open on what the dashboard shows, ready to be narrowed.
 */
export const timeRangeBounds = (value: string | undefined, now: Date): AbsoluteTimeRange => {
  const absolute = value ? parseAbsoluteTimeRange(value) : undefined;
  if (absolute) return absolute;
  const minutes = value ? timeRangeMinutes(value) : Number.POSITIVE_INFINITY;
  const spanMinutes = Number.isFinite(minutes) ? minutes : FALLBACK_SPAN_MINUTES;
  const end = new Date(Math.floor(now.getTime() / MINUTE_MS) * MINUTE_MS);
  return { start: new Date(end.getTime() - spanMinutes * MINUTE_MS), end };
};

const Form = styled.form`
  padding: ${spacing.r8} ${spacing.r12};
  min-width: 15rem;
`;

/** The app is dark-only: the browser's picker and calendar icon must be too. */
const DateTimeInput = styled(Input)`
  color-scheme: dark;
`;

type AbsoluteRangeFormProps = {
  initialRange: AbsoluteTimeRange;
  onApply: (value: string) => void;
};

/**
 * Two native `datetime-local` fields in the reader's zone. The range they produce is sent in UTC,
 * so a shared link means the same instants wherever it is opened. The end is not capped at the
 * reader's clock, which can run behind the deployment's.
 */
export const AbsoluteRangeForm: React.FC<AbsoluteRangeFormProps> = ({ initialRange, onApply }) => {
  const fromId = useId();
  const toId = useId();
  const [from, setFrom] = useState(() => toDateTimeInputValue(initialRange.start));
  const [to, setTo] = useState(() => toDateTimeInputValue(initialRange.end));

  const start = fromDateTimeInputValue(from);
  const end = fromDateTimeInputValue(to);
  const isOrdered = start !== undefined && end !== undefined && start < end;
  const isReversed = start !== undefined && end !== undefined && !isOrdered;

  const applyRange = (event: React.FormEvent) => {
    event.preventDefault();
    if (isOrdered) onApply(toAbsoluteTimeRange({ start, end }));
  };

  return (
    <Form aria-label="Absolute range" onSubmit={applyRange}>
      <Stack direction="vertical" gap="r8">
        <Text variant="Smaller" color="textSecondary">
          Absolute range
        </Text>
        <Stack direction="vertical" gap="r4">
          <Text variant="Smaller" color="textSecondary" as="label" htmlFor={fromId}>
            From
          </Text>
          <DateTimeInput
            id={fromId}
            type="datetime-local"
            required
            fluid
            value={from}
            max={to || undefined}
            onChange={(event) => setFrom(event.target.value)}
          />
        </Stack>
        <Stack direction="vertical" gap="r4">
          <Text variant="Smaller" color="textSecondary" as="label" htmlFor={toId}>
            To
          </Text>
          <DateTimeInput
            id={toId}
            type="datetime-local"
            required
            fluid
            value={to}
            min={from || undefined}
            error={isReversed ? REVERSED_BOUNDS : undefined}
            onChange={(event) => setTo(event.target.value)}
          />
        </Stack>
        {isReversed && (
          <Text variant="Smaller" color="statusCritical">
            {REVERSED_BOUNDS}
          </Text>
        )}
        <Button
          type="submit"
          variant="secondary"
          label="Apply range"
          disabled={!isOrdered}
          style={{ alignSelf: 'flex-start' }}
        />
      </Stack>
    </Form>
  );
};
